// โครงสร้างข้อมูลสำหรับระบบตลาดค้าส่ง

/**
 * โครงสร้างข้อมูล Store (ร้านค้า)
 */
export const createStoreModel = (data) => {
  return {
    // ข้อมูลพื้นฐาน
    name: data.name || '',
    description: data.description || '',
    ownerId: data.ownerId || '',
    ownerRole: data.ownerRole || '', // farm, middleman, store_owner
    
    // ข้อมูลติดต่อ
    contact: {
      phone: data.contact?.phone || '',
      email: data.contact?.email || '',
      lineId: data.contact?.lineId || '',
      address: data.contact?.address || ''
    },
    
    // ข้อมูลร้าน
    logo: data.logo || '',
    coverImage: data.coverImage || '',
    category: data.category || '', // หมวดหมู่สินค้า
    
    // ข้อมูลการชำระเงิน
    payment: {
      // วิธีการชำระเงินที่รองรับ
      methods: data.payment?.methods || ['bank_transfer'], // ['bank_transfer', 'qr_code', 'cash_on_delivery', 'pickup']
      
      // บัญชีธนาคาร (รองรับหลายบัญชี)
      bankAccounts: data.payment?.bankAccounts || [],
      // Format: [{ bank: 'กรุงไทย', accountNumber: '123-4-56789-0', accountName: 'ชื่อบัญชี' }]
      
      // QR Code PromptPay (สำหรับร้านที่สมัคร package)
      qrCodes: data.payment?.qrCodes || [],
      // Format: [{ 
      //   type: 'upload' | 'generate', // วิธีสร้าง QR Code
      //   qrCode: 'url', // สำหรับ upload
      //   promptPayNumber: '', // สำหรับ generate (เบอร์โทรหรือเลขบัตรประชาชน)
      //   amount: 0, // ยอดเงิน (สำหรับ generate จะแสดงใน QR Code)
      //   description: '' 
      // }]
      
      // จ่ายสด นัดรับ
      cashOnPickup: data.payment?.cashOnPickup || false,
      pickupLocation: data.payment?.pickupLocation || '',
      pickupTime: data.payment?.pickupTime || '',
      
      // เก็บปลายทาง (สำหรับร้านที่สมัคร package)
      cashOnDelivery: data.payment?.cashOnDelivery || false,
      codCourierCompanies: data.payment?.codCourierCompanies || [], // ['J&T', 'Flash', ...]
      
      // Admin Middleman (สำหรับร้านที่สมัคร package)
      adminMiddleman: data.payment?.adminMiddleman || {
        enabled: false,
        feeRate: 0.05 // 5% ค่าธรรมเนียม
      }
    },
    
    // ข้อมูลการจัดส่ง
    shipping: {
      methods: data.shipping?.methods || [], // ['courier', 'pickup', 'special_vehicle']
      courierCompanies: data.shipping?.courierCompanies || [], // ['J&T', 'Flash', 'Kerry', ...]
      pickupLocation: data.shipping?.pickupLocation || '',
      pickupTime: data.shipping?.pickupTime || '',
      specialVehicleInfo: data.shipping?.specialVehicleInfo || '',
      fees: {
        courier: data.shipping?.fees?.courier || { type: 'fixed', amount: 0 }, // { type: 'fixed'|'rate', amount: number }
        pickup: data.shipping?.fees?.pickup || { type: 'fixed', amount: 0 },
        specialVehicle: data.shipping?.fees?.specialVehicle || { type: 'fixed', amount: 0 }
      }
    },
    
    // สถานะ
    status: data.status || 'active', // pending, active, suspended, closed (default เป็น active เมื่อเปิดร้าน)
    isVerified: data.isVerified || false,
    
    // ข้อมูลการตรวจสอบ (Store Verification System)
    verification: {
      status: data.verification?.status || 'unverified', // unverified, pending, in_review, approved, rejected, expired
      verificationId: data.verification?.verificationId || null, // reference to storeVerifications collection
      verificationScore: data.verification?.verificationScore || 0, // 0-175
      riskScore: data.verification?.riskScore || 100, // 0-100 (100 = ปลอดภัย)
      verifiedAt: data.verification?.verifiedAt || null,
      expiresAt: data.verification?.expiresAt || null,
    },
    
    // ข้อมูล Level/Reputation
    reputationScore: 0,
    reputationLevel: 1,
    completedOrders: 0,
    averageRating: 0,
    totalReviews: 0,
    onTimeDeliveryRate: 0,
    monthsOpen: 0,
    
    // ข้อมูลการจัดการ
    createdAt: data.createdAt || new Date(),
    updatedAt: new Date(),
    openedAt: data.openedAt || null
  };
};

/**
 * โครงสร้างข้อมูล Product (สินค้า)
 */
export const createProductModel = (data) => {
  return {
    // ข้อมูลพื้นฐาน
    name: data.name || '',
    description: data.description || '',
    storeId: data.storeId || '',
    storeName: data.storeName || '',
    
    // ราคาและจำนวน
    price: data.price || 0,
    quantity: data.quantity || 0,
    unit: data.unit || 'กรัม', // กรัม, กิโลกรัม, ต้น
    minimumOrderQuantity: data.minimumOrderQuantity || null, // สั่งซื้อขั้นต่ำ
    
    // หมวดหมู่
    category: data.category || '', // uniforms, equipment, shoes, accessories, themes, items (Nurse App) หรือ Flowers, Seeds, etc. (Marketplace)
    subcategory: data.subcategory || '', // shirts, pants, stethoscope, etc.
    strain: data.strain || '', // สายพันธุ์ (สำหรับ Marketplace เดิม)
    type: data.type || '', // Sativa, Indica, Hybrid (สำหรับ Marketplace เดิม)
    
    // รูปภาพ
    images: data.images || [],
    
    // ข้อมูลเพิ่มเติม
    harvestDate: data.harvestDate || null,
    expiryDate: data.expiryDate || null,
    certificate: data.certificate || '', // ใบรับรอง
    
    // สถานะ
    status: data.status || 'active', // active, sold_out, auction, inactive
    isAuction: data.isAuction || false,
    auctionId: data.auctionId || null,
    
    // ข้อมูลการขาย
    views: 0,
    sales: 0,
    
    // วิธีการชำระเงินและการจัดส่ง (เลือกจาก store settings)
    paymentMethods: data.paymentMethods || [], // ['bank_transfer', 'qr_code', 'cash_on_pickup', 'cash_on_delivery']
    shippingMethods: data.shippingMethods || [], // ['courier', 'pickup', 'special_vehicle']
    
    // ข้อมูลการจัดการ
    createdAt: data.createdAt || new Date(),
    updatedAt: new Date()
  };
};

/**
 * โครงสร้างข้อมูล Auction (ประมูล)
 */
export const createAuctionModel = (data) => {
  return {
    // ข้อมูลพื้นฐาน
    productId: data.productId || '',
    productName: data.productName || '',
    storeId: data.storeId || '',
    sellerId: data.sellerId || '',
    
    // ข้อมูลประมูล
    startingPrice: data.startingPrice || 0,
    currentBid: data.startingPrice || 0,
    reservePrice: data.reservePrice || null, // ราคาขั้นต่ำ (ถ้ามี)
    
    // ประเภทประมูล
    auctionType: data.auctionType || 'highest_bid', // highest_bid, auction
    // highest_bid: ผู้เสนอราคาสูงสุดชนะ
    // auction: ผู้เสนอราคาสูงสุดชนะ แต่แสดงราคาทั้งหมด
    
    // เวลา
    startTime: data.startTime || new Date(),
    endTime: data.endTime || null, // null = ไม่จำกัดเวลา
    duration: data.duration || 24, // ชั่วโมง
    
    // ผู้เสนอราคา
    bids: data.bids || [], // [{ userId, amount, timestamp }]
    highestBidder: data.highestBidder || null,
    
    // สถานะ
    status: data.status || 'active', // active, ended, cancelled
    isEnded: false,
    
    // ค่าธรรมเนียม
    sellerFeePaid: data.sellerFeePaid || false,
    bidFees: data.bidFees || {}, // { userId: { paid: boolean, refunded: boolean } }
    
    // ข้อมูลการจัดการ
    createdAt: data.createdAt || new Date(),
    updatedAt: new Date()
  };
};

/**
 * โครงสร้างข้อมูล Order (ออเดอร์)
 */
export const createOrderModel = (data) => {
  return {
    // ข้อมูลพื้นฐาน
    orderNumber: data.orderNumber || `ORD-${Date.now()}`,
    buyerId: data.buyerId || '',
    buyerName: data.buyerName || '',
    sellerId: data.sellerId || '',
    sellerName: data.sellerName || '',
    storeId: data.storeId || '',
    storeName: data.storeName || '',
    
    // รายการสินค้า
    items: data.items || [], // [{ productId, name, price, quantity, subtotal }]
    
    // ราคา
    subtotal: data.subtotal || 0,
    fee: data.fee || 0, // ค่าธรรมเนียมเหรียญ
    total: data.total || 0,
    
    // สถานะ
    status: data.status || 'pending_payment', // pending_payment, paid, processing, shipping, delivered, received, cancelled
    paymentStatus: data.paymentStatus || 'pending', // pending, paid, failed
    
    // การชำระเงิน
    paymentMethod: data.paymentMethod || 'bank_transfer',
    paymentProof: data.paymentProof || null, // URL รูปภาพหลักฐานการโอน
    paymentDate: data.paymentDate || null,
    adminFee: data.adminFee || 0, // ค่าธรรมเนียมแอดมิน (ถ้าใช้ admin middleman)
    
    // การจัดส่ง
    shippingMethod: data.shippingMethod || '', // courier, pickup, special_vehicle
    shippingAddress: data.shippingAddress || '',
    buyerPhone: data.buyerPhone || '', // เบอร์โทรผู้รับ (สำหรับ QR Code บน sticker)
    trackingNumber: data.trackingNumber || '',
    courierCompany: data.courierCompany || null, // 'J&T', 'Flash', 'Kerry', 'DHL', 'FedEx', 'Nim Express', 'ไปรษณีย์ไทย'
    customCourierCompany: data.customCourierCompany || null, // สำหรับกรอกบริษัทขนส่งเอง
    shippingDate: data.shippingDate || null,
    deliveredDate: data.deliveredDate || null,
    receivedDate: data.receivedDate || null, // วันที่ผู้ซื้อรับของ
    receivedBy: data.receivedBy || null, // userId ของผู้ซื้อที่กดรับของ
    
    // ข้อมูลเพิ่มเติม
    notes: data.notes || '',
    
    // ข้อมูลการจัดการ
    createdAt: data.createdAt || new Date(),
    updatedAt: new Date()
  };
};

/**
 * โครงสร้างข้อมูล Buyer Request (ประกาศหาสินค้า)
 */
export const createBuyerRequestModel = (data) => {
  return {
    // ข้อมูลพื้นฐาน
    buyerId: data.buyerId || '',
    buyerName: data.buyerName || '',
    
    // ข้อมูลสินค้าที่ต้องการ
    productName: data.productName || '',
    description: data.description || '',
    category: data.category || '',
    quantity: data.quantity || 0,
    unit: data.unit || 'กรัม',
    desiredPrice: data.desiredPrice || null, // ราคาที่ต้องการ (ถ้ามี)
    
    // เวลา
    startDate: data.startDate || new Date(),
    endDate: data.endDate || null, // null = ไม่จำกัดเวลา
    duration: data.duration || 7, // วัน
    
    // ผู้เสนอราคา
    offers: data.offers || [], // [{ sellerId, storeId, price, message, timestamp }]
    acceptedOffer: data.acceptedOffer || null, // ID ของ offer ที่ยอมรับ
    
    // สถานะ
    status: data.status || 'active', // active, closed, fulfilled
    isFulfilled: false,
    
    // ค่าธรรมเนียม
    feePaid: data.feePaid || false,
    
    // ข้อมูลการจัดการ
    createdAt: data.createdAt || new Date(),
    updatedAt: new Date()
  };
};

/**
 * โครงสร้างข้อมูล Cart Item (ตะกร้า)
 */
export const createCartItemModel = (data) => {
  return {
    productId: data.productId || '',
    productName: data.productName || '',
    storeId: data.storeId || '',
    storeName: data.storeName || '',
    price: data.price || 0,
    quantity: data.quantity || 1,
    image: data.image || '',
    unit: data.unit || 'กรัม',
    subtotal: (data.price || 0) * (data.quantity || 1)
  };
};

